import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
} from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Store } from "@ngrx/store";
import { GridOptions } from "ag-grid-enterprise";
import {
  Pagination} from "blg-akaun-ts-lib";
import { ToastrService } from "ngx-toastr";
import { SessionSelectors } from "../../../session/session-controller/selectors";
import { SessionStates } from "../../../session/session-controller/states";
import { ViewColumnComponent } from "../../../../view-column.component";
import { AppConfig } from "../../../../visa";
import { from, Observable } from "rxjs";
import { map, mergeMap, toArray } from "rxjs/operators";
import { SubSink } from "subsink2";
import { AgingPeriodFacade } from "../../facades/aging-period.facade";
import { agingriodPeriodSearchModel } from "../../../../models/advanced-search-models/aging-period-search.model";
import { SessionActions } from "../../../session/session-controller/actions";
import { ListingInputModel } from '../../../../models/listing-input.model';
import { SearchQueryModel } from '../../../../models/query.model';
import { ListingService } from '../../../../services/listing-service';
import { PaginationV2Component } from "../../../../utilities/pagination-v2/pagination-v2.component";
import { SharedUtilitiesModule } from "../../../../utilities/shared-utilities.module";
import { AdvancedSearchGeneralComponent } from '../../../../utilities/advanced-search-general/advanced-search-general.component'
import { AgingPeriodStates } from '../../aging-period-controller/states';
import { AgingPeriodActions } from '../../aging-period-controller/actions';
import { SharedUtilitiesHelperFunctions } from "../../../../utilities/helper-functions";

interface LocalState {
  deactivateAdd: boolean;
  deactivateList: boolean;
  selectedRow: any;
}

@Component({
  selector: "app-aging-period-listing",
  templateUrl: "./aging-period-listing.component.html",
  styleUrls: ["./aging-period-listing.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore],
})
export class AgingPeriodListingComponent extends ViewColumnComponent {
  @ViewChild(AdvancedSearchGeneralComponent) advancedSearch!: AdvancedSearchGeneralComponent;

  protected subs = new SubSink();

  protected compName = "Aging Period Listing";
  protected readonly index = 0;
  protected localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(
    (state) => state.deactivateAdd
  );

  toggleColumn$: Observable<boolean>;
  searchModel = agingriodPeriodSearchModel;

  pagination = new Pagination();

  permissions: any[] = [];

  readPermissionDefintion = {
    branch: 'API_TNT_DM_AGING_PERIOD_read'
  }


  @ViewChild(PaginationV2Component, {static: false})
  private paginationComponent: PaginationV2Component;

  branchGuids: any[];
  hasCreatePermission: boolean;
  failedCreditLimitCheck$: Observable<string[]>;


  rowData = [];
  totalRecords = 0;
  limit = 25;
  searchQuery: SearchQueryModel;
  firstLoad;

  // api visa
  apiVisa = AppConfig.apiVisa;

  // initial grid state
  gridApi;
  gridColumnApi;


  gridOptions: GridOptions = {
    statusBar: {
      statusPanels: [
          { statusPanel: 'agTotalAndFilteredRowCountComponent', key: 'totalAndFilter', align: 'left' },
          { statusPanel: 'agSelectedRowCountComponent', align: 'left' },
          { statusPanel: 'agAggregationComponent', align: 'right' },
      ]
    },
    defaultColDef: {
        filter: 'agTextColumnFilter',
        floatingFilter: false,
        //minWidth: 140,
        //flex: 1,
        width: 100,
        sortable: true,
        resizable: true,
        suppressSizeToFit: false,
    },
    columnTypes: SharedUtilitiesHelperFunctions.columnTypes,
    defaultExcelExportParams: SharedUtilitiesHelperFunctions.getDefaultExportParams(),
    defaultCsvExportParams: SharedUtilitiesHelperFunctions.getDefaultExportParams(),
    suppressAggFuncInHeader: true,
    pagination: true,
    animateRows: true,
    sideBar: true,
    rowSelection: 'single',
    suppressRowClickSelection: false,
    multiSortKey: 'ctrl',
    onSortChanged: this.onSortChanged,
    onFilterChanged: this.onFilterChanged,
  }

  columnsDefs = [
    { headerName: 'Default Selection', field: 'default_selection', checkboxSelection: true },
    {
      headerName: "Aging Period Name",
      field: "name",
      type: 'textColumn'
    },
    {
      headerName: "Aging Period Type",
      field: "period_type",
      type: 'textColumn'
    }
  ];
  AGING_PERIOD_HDR_GUID;
  constructor(
    private readonly sessionStore: Store<SessionStates>,
    private store: Store<AgingPeriodStates>,
    private viewColFacade: AgingPeriodFacade,
    private toastr: ToastrService,
    private listingService: ListingService,
    private readonly componentStore: ComponentStore<LocalState>,
  ) {
    super();
  }

  ngOnInit() {
    this.subs.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState(a);
    });
    this.subs.sink = this.sessionStore
    .select(SessionSelectors.selectMasterSettings)
    .subscribe(master=>{
      this.AGING_PERIOD_HDR_GUID = master?.AGING_PERIOD_HDR_GUID;
    })
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    this.gridColumnApi = params.columnApi;

    this.clear();
    this.getTotalData();
    this.createData();


    this.gridApi.sizeColumnsToFit();
    SharedUtilitiesHelperFunctions.autoSizeAllColumns(params);
  }

  onToggle(e: boolean) {
    this.viewColFacade.toggleColumn(e);
  }

  createData() {
    const formData = this.getInputModel();
    this.subs.sink = this.listingService.get("aging-period/backoffice-ep", formData, this.apiVisa).pipe(
	  mergeMap(a => from(a.data).pipe(
        map(b => {
          return b;
        }),
        toArray(),
        map(c => {
          a.data = c;
          return a;
        })
      ))
    ).subscribe(resolved => {
      console.log(resolved);
      this.rowData = [...this.rowData, ...resolved.data];
      this.gridApi.setRowData(this.rowData);
      this.gridApi.forEachNode(a => {
        if (a.data && a.data?.guid === this.AGING_PERIOD_HDR_GUID) {
          a.setSelected(true);
        }
      });
      this.gridApi.paginationGoToLastPage();
      this.paginationComponent.totalRecords.next(this.totalRecords);
    }, err => {
      console.error(err);
    });
  };

  getInputModel() {
    const inputModel = {} as ListingInputModel;
    inputModel.search = (this.searchQuery?.keyword);
    inputModel.searchColumns = ["name"];
    inputModel.status = ['ACTIVE'];
    inputModel.orderBy = 'updated_date';
    inputModel.order = 'desc';
    inputModel.limit = this.limit;
    inputModel.offset = this.rowData.length;
    inputModel.calcTotalRecords = false;
    inputModel.showCreatedBy = false;
    inputModel.showUpdatedBy = false;
    inputModel.filterLogical = 'AND';
    inputModel.filterConditions = [];
    inputModel.childs = [];
    inputModel.joins= []
    inputModel.childs =[];


    return inputModel;
  }

  getTotalData() {
    console.log("on create data....");
    const formData = this.getInputModel();
    formData.childs = [];
    formData.joins = [];

    formData.calcTotalRecords = true;
    formData.calcTotalRecordsOnly = true;
    this.subs.sink = this.listingService.get("aging-period/backoffice-ep", formData, this.apiVisa).pipe(
    ).subscribe(resolved => {
      this.totalRecords = resolved.totalRecords;
      this.paginationComponent.totalRecords.next(this.totalRecords);
    }, err => {
      console.error(err);
    });
  };

  onSearch(e: SearchQueryModel) {

    if (!e.isEmpty) {
      if (e.keyword && e.keyword.length > 0 && e.keyword.length < 3)  {
        this.toastr.error(
          'Search keyword must more than 2 characters.',
          'Keyword',
          {
            tapToDismiss: true,
            progressBar: true,
            timeOut: 1300
          }
        );
        return;
      }
      this.firstLoad = false;

      this.searchQuery = e;
    } else {
      this.firstLoad = true;

      this.searchQuery = null;
    }
    this.clear();
    this.getTotalData();
    this.createData();
  }

  clear() {
    this.gridApi.setRowData(null);
    this.totalRecords = 0;
    this.rowData = [];
    this.paginationComponent.totalRecords.next(this.totalRecords);
  }

  onAdd() {
    this.viewColFacade.updateInstance(this.index, {
      ...this.localState,
      deactivateAdd: true,
      deactivateList: false
    });
    this.viewColFacade.onNextAndReset(this.index, 1);
  }

  onRowClicked(container) {
    this.store.dispatch(AgingPeriodActions.selectAgingPeriod({ container }));
    this.viewColFacade.updateInstance(this.index, {
      ...this.localState,
      deactivateAdd: true,
      deactivateList: false
    });
    this.viewColFacade.onNextAndReset(this.index, 2);
  }

  onFirstDataRendered(params) {
    params.columnApi.autoSizeAllColumns();
  }

  onSortChanged(e) {
    e.api.refreshCells();
  }

  onFilterChanged(e) {
    e.api.refreshCells();
  }

  onMorePage() {
    if (this.rowData.length < this.totalRecords) {
      this.createData();
    }
  }

  onSelectionChange(event) {
    if (event.api.getSelectedRows().length > 0) {
      const selectedGuid = event.api.getSelectedRows()[0].guid.toString();
      if(this.AGING_PERIOD_HDR_GUID!==selectedGuid){
        this.store.dispatch(
          SessionActions.saveMasterSettingsInit({ settings: {AGING_PERIOD_HDR_GUID: selectedGuid} })
        );
      }
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
